import { EntityName } from '@provision/entity-types/dist/entity.types';
import { EventHubEntityChangePayloadObject } from '@provision/event-hub-utils';
import { portalPaths } from '@provision/portal-routes';
import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { MetaTags } from 'src/components';

import { Conversation } from 'src/graphql/entities/conversation';
import { useConversationUpdate } from 'src/graphql/entities/conversation/use-conversation-update';
import { CustomView } from 'src/graphql/entities/custom-view';
import { EntityNames } from 'src/graphql/types';
import { ConversationWindow } from 'src/pages/Conversations/conversation';
import { EntityWindowWrapper } from 'src/pages/Conversations/shared-layout';
import { useSelectedConversation } from 'src/pages/Conversations/use-selected-conversation';
import { BaseSelectedView } from 'src/pages/CustomViews/custom-views-selected-view/base-selected-view';
import { CreateButton } from 'src/pages/CustomViews/custom-views-selected-view/components/create-button';

import { useCustomViewState } from 'src/pages/CustomViews/hooks/use-custom-view-state';
import { GenericModalSet } from 'src/pages/Search/generic-modal-set';
import { useGlobalStore } from 'src/store';
import {
  TabType,
  useGlobalTabs,
  useNotifications,
  useOnDeleteSubscription,
  useOnEsc,
} from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';
import { capitalizeString } from 'src/utils/strings';
import { Platform } from 'src/utils/system';

export const ConversationsViewInner = ({
  conversationFriendlyId,
  conversationId,
  customView,
  viewId,
  handleConversationClick,
  onCloseConversation,
  title,
}: {
  conversationFriendlyId?: string;
  conversationId?: string;
  viewId?: string;
  title?: string;
  customView?: CustomView;
  handleConversationClick: (id: string, conversation: Conversation) => void;
  onCloseConversation: () => void;
}) => {
  const [localConversation, setLocalConversation] = useState<Conversation | null>(null);

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showInfoAlert } = useNotifications();
  const { setDefaultPartnerFilter } = useCustomViewState({
    id: viewId || customView?.customViewId!,
    customView,
    entity: customView?.entityName || EntityNames.Conversation,
  });

  const { selectedConversation: rawSelected, isLoading: isSelectedConversationLoading } =
    useSelectedConversation({
      conversationId: conversationFriendlyId!,
      includeAttachments: true,
    });

  const selectedConversation = useMemo(() => {
    if (!conversationFriendlyId) {
      return null;
    }
    return rawSelected || localConversation;
  }, [conversationFriendlyId, localConversation, rawSelected]);

  useEffect(() => {
    if (isNullish(selectedPartnerId)) {
      setDefaultPartnerFilter(selectedConversation?.partner);
    }
  }, [setDefaultPartnerFilter, selectedConversation, selectedPartnerId]);

  const handleConversationClose = useCallback(() => {
    onCloseConversation();
    setDefaultPartnerFilter(null);
  }, [onCloseConversation, setDefaultPartnerFilter]);

  const { updateConversation } = useConversationUpdate();

  const { partnerPrefix } = useNavigateRoute();

  const { openInTab } = useGlobalTabs();

  const handleClick = useCallback(
    (id: string, record: Conversation, event?: MouseEvent) => {
      if (Platform.isMacOS ? event?.metaKey : event?.ctrlKey) {
        openInTab(
          { entity: record, type: TabType.Conversation },
          { customViewId: customView?.customViewId! },
          false,
        );
        return;
      }
      if (event?.shiftKey) {
        window.open(
          `${partnerPrefix ? `/${partnerPrefix}` : ''}${portalPaths.conversation.full(record.friendlyId)}`,
          '_blank',
        );
        return;
      }
      handleConversationClick(id, record);
      setLocalConversation(record);
    },
    [customView?.customViewId, handleConversationClick, openInTab, partnerPrefix],
  );

  const handleClose = useCallback(() => {
    handleConversationClose();
    setLocalConversation(null);
  }, [handleConversationClose]);

  useOnDeleteSubscription({
    entityName: capitalizeString(EntityNames.Conversation) as EntityName,
    filter: useCallback(
      (message: EventHubEntityChangePayloadObject) =>
        message.previousAttributes.id === conversationId,
      [conversationId],
    ),
    callback: useCallback(() => {
      handleClose();
      showInfoAlert('Conversation was deleted');
    }, [handleClose, showInfoAlert]),
  });

  const onUpdate = useCallback(
    async (conversation: Conversation) => {
      await updateConversation(conversation);
    },
    [updateConversation],
  );

  useOnEsc(handleClose);

  const rightPanel = useMemo(() => {
    if (!selectedConversation) {
      return;
    }

    return (
      <EntityWindowWrapper>
        <ConversationWindow
          key={selectedConversation.friendlyId}
          customViewId={customView?.customViewId}
          customViewName={customView?.name}
          onUpdate={onUpdate}
          noticeMessage={
            !selectedConversation && !isSelectedConversationLoading
              ? 'The conversation no longer matches your view’s filters.'
              : ''
          }
          conversation={selectedConversation}
          onCloseConversation={handleClose}
          isSplitView
        />
      </EntityWindowWrapper>
    );
  }, [
    selectedConversation,
    customView?.customViewId,
    customView?.name,
    onUpdate,
    isSelectedConversationLoading,
    handleClose,
  ]);

  const slots = useMemo(
    () => ({
      rightPanel,
      createButton: <CreateButton icon="add_comment" />,
    }),
    [rightPanel],
  );

  return (
    <MetaTags title={selectedConversation?.subject || customView?.name || 'Conversations'}>
      <GenericModalSet
        entityName={EntityNames.Conversation}
        selectedRecord={selectedConversation}
      />
      <BaseSelectedView<Conversation>
        entity={EntityNames.Conversation}
        viewId={viewId || customView?.customViewId!}
        selectedRecord={selectedConversation}
        onRecordClick={handleClick}
        onRecordClose={handleClose}
        title={title}
        slots={slots}
      />
    </MetaTags>
  );
};
